import React, { useState, useContext } from 'react';
import styled, { useTheme, DefaultTheme } from 'styled-components';
import { Puff } from 'react-loader-spinner';
import { postcodeValidator } from 'postcode-validator';
import { DealerSearchContext } from '@rsc/contexts/DealerSearchContext';
import { Dealer as AlgoliaDealer } from '@rsc/models/algolia/Dealer';
import { useUiComponentsInternationalContext } from '@rsc/static-translations';
import { SiteSettingsContext } from '@rsc/contexts/SiteSettingsContext';
import IconSearch from '../svgs/search-icon.svg';
import SearchResults from './SearchResults';
import { stripFormattingFromPhoneNumber } from '@rsc/utils/stripFormattingFromPhoneNumber';

const SearchBox = styled.div`
  label {
    margin-bottom: 0.5rem;
    span {
      display: block;
      margin-bottom: 0.25rem;
    }
  }
  p.error {
    color: red;
    font-size: smaller;
    padding: 0.25rem 0;
  }
`;

const InputGroup = styled.div`
  display: flex;
  flex-direction: row;
  input {
    border: 1px solid ${({ theme }) => theme.colors.inputBorder};
    border-right: 0;
    border-top-left-radius: 0.25rem;
    border-bottom-left-radius: 0.25rem;
    color: ${({ theme }) => theme.colors.inputFontColor};
    padding: 0.65rem 0.5rem;
    flex: 1;
    max-height: 3rem;
    &.has-error {
      border-color: red;
    }
  }
  button {
    background-color: ${({ theme }) => theme.colors.brandPrimary};
    z-index: 1;
    width: 2.75rem;
    border: 1px solid ${({ theme }) => theme.colors.brandPrimary};
    border-left: 0;
    border-top-right-radius: 0.25rem;
    border-bottom-right-radius: 0.25rem;
    max-height: 3rem;
    display: flex;
    align-items: center;
    justify-content: center;
  }
`;

const NoResults = styled.div`
  padding: 1.5rem 0;
  h2 {
    font-weight: bold;
    margin-bottom: 0.25rem;
  }
  p {
    font-size: 1.125rem;
  }
`;

const SpinnerContainer = styled.div`
  padding-top: 1rem;
  display: flex;
  justify-content: center;
  align-items: center;
  flex: 1;
`;

export interface DealerSearchViewProps {
  setDealerSearchActive: React.Dispatch<React.SetStateAction<boolean>>;
  deleteDealerSearchResults: () => void;
  searchTerm: string;
  setFormHasError: React.Dispatch<React.SetStateAction<boolean>>;
  getDealerSearchResults: (zipcode: string) => Promise<void>;
  formHasError: boolean;
  setSearchTerm: React.Dispatch<React.SetStateAction<string>>;
  dealerSearchResults: AlgoliaDealer[];
  loading: boolean;
  firstLoad: boolean;
  displayMode: string;
}

interface ThemeProps extends DefaultTheme {
  colors: {
    brandPrimary: string;
  };
}

function DealerSearchView({
  setDealerSearchActive,
  deleteDealerSearchResults,
  searchTerm,
  setFormHasError,
  getDealerSearchResults,
  formHasError,
  setSearchTerm,
  dealerSearchResults,
  loading,
  firstLoad,
  displayMode,
}: Readonly<DealerSearchViewProps>) {
  const { tt } = useUiComponentsInternationalContext();
  const { siteSettings } = useContext(SiteSettingsContext);

  const theme = useTheme() as ThemeProps;
  const iconColor = theme.colors.brandPrimary;
  return (
    <>
      <SearchBox>
        <form
          onSubmit={e => {
            e.preventDefault();
            deleteDealerSearchResults();

            if (
              // check if valid US or CA postal code
              postcodeValidator(searchTerm.trim(), 'CA') ||
              postcodeValidator(searchTerm.trim(), 'US')
            ) {
              // clear existing error
              setFormHasError(false);

              // if Canadian postal code, ensure the string is valid and has a space in the center
              if (postcodeValidator(searchTerm.trim(), 'CA')) {
                getDealerSearchResults(
                  searchTerm
                    .toUpperCase()
                    .trim()
                    .replace(/ /g, '')
                    .replace(/(.{3})/, '$1 ')
                );
              } else {
                // otherwise, send full zip code
                getDealerSearchResults(searchTerm);
              }
            } else {
              // trigger zip/postcode error
              setFormHasError(true);
            }
          }}
          role="search"
        >
          <label htmlFor="dealer-search-input">
            <span>{tt(s => s.dealerCommon.searchByZipCodeLabel)}</span>
            <InputGroup>
              <input
                type="text"
                id="dealer-search-input"
                name="dealer-search-input"
                autoComplete="postal-code"
                aria-describedby={
                  formHasError ? 'dealer-search-error' : undefined
                }
                value={searchTerm}
                className={formHasError ? 'has-error' : ''}
                onChange={e => {
                  setSearchTerm(e.target.value);
                }}
                maxLength={7}
                ref={input => {
                  if (input && firstLoad) {
                    input.focus();
                  }
                }}
              />
              <button
                type="submit"
                aria-label={tt(s => s.dealerCommon.submitAriaLabel)}
              >
                <IconSearch
                  width={16}
                  height={16}
                  fill="#fff"
                  aria-hidden="true"
                />
              </button>
            </InputGroup>
          </label>
          {formHasError && (
            <p id="dealer-search-error" className="error" role="alert">
              {tt(s => s.dealerCommon.enterValidZipCodeValidationMessage)}
            </p>
          )}
        </form>
      </SearchBox>
      {loading && (
        <SpinnerContainer>
          <Puff color={iconColor} height={60} width={60} />
        </SpinnerContainer>
      )}
      {dealerSearchResults && dealerSearchResults.length > 0 && (
        <SearchResults
          dealerSearchResults={dealerSearchResults}
          displayMode={displayMode}
          setDealerSearchActive={setDealerSearchActive}
        />
      )}
      {!loading &&
        !firstLoad &&
        !formHasError &&
        dealerSearchResults &&
        dealerSearchResults.length === 0 && (
          <NoResults>
            <h2>{tt(s => s.dealerCommon.weAreSorryHeader)}</h2>
            <p>
              {tt(s => s.dealerCommon.searchNotFoundMessage, {
                phone: (
                  <a
                    href={`tel:+${stripFormattingFromPhoneNumber(siteSettings.phone)}`}
                  >
                    {siteSettings.phone}
                  </a>
                ),
              })}
            </p>
          </NoResults>
        )}
    </>
  );
}

interface DealerSearchContextProps {
  dealerSearchResults;
  getDealerSearchResults;
  deleteDealerSearchResults;
  firstLoad;
  loading;
  setDealerSearchActive;
}

export interface DealerSearchProps {
  displayMode: string;
}

function DealerSearch({ displayMode }: Readonly<DealerSearchProps>) {
  const {
    dealerSearchResults,
    getDealerSearchResults,
    deleteDealerSearchResults,
    firstLoad,
    loading,
    setDealerSearchActive,
  } = useContext<DealerSearchContextProps>(DealerSearchContext);

  const [searchTerm, setSearchTerm] = useState('');
  const [formHasError, setFormHasError] = useState(false);

  return (
    <DealerSearchView
      deleteDealerSearchResults={deleteDealerSearchResults}
      searchTerm={searchTerm}
      setFormHasError={setFormHasError}
      getDealerSearchResults={getDealerSearchResults}
      formHasError={formHasError}
      setSearchTerm={setSearchTerm}
      dealerSearchResults={dealerSearchResults}
      loading={loading}
      firstLoad={firstLoad}
      displayMode={displayMode}
      setDealerSearchActive={setDealerSearchActive}
    />
  );
}

export default DealerSearch;
